import { SettingComponentMap } from "@bundles/constant";
import { getFormElementFromDataComp, resolveConfig } from "@libs/helper";
import {
  THEME_COLLAPSIBLE_SECTION_OPENED,
  THEME_SIDEBAR_PANEL_READY,
  type ThemeCollapsibleSectionOpenedDetail,
  type ThemeSidebarPanelReadyDetail,
} from "@libs/themeSidebarPanelEvents";
import { FD_SELECTED_OVERLAY_PAIR_CSS } from "../stores/themes/shared/themeBuilder/components/themePreview/themePreviewOverlayStyles";
import { scrollPreviewHtmlElementIntoView } from "../stores/themes/shared/themeBuilder/components/themePreview/themePreviewScrollIntoView";
import {
  filterToOutermostElements,
  SELECTED_OVERLAY_PADDING,
  unionBoundingRect,
  updateOverlayBox,
  updateSelectedOverlayFromRect,
  viewportRectToHostLocal,
} from "../stores/themes/shared/themeBuilder/components/themePreview/themePreviewSelectedOverlayHelpers";
import { attachSelectedLayoutObservers } from "../stores/themes/shared/themeBuilder/components/themePreview/themePreviewSelectedOverlayObservers";
import { clearThemePreviewClickSelection } from "../stores/themes/shared/themeBuilder/components/themePreview/themePreviewClickSelectionRegistry";

/** Đồng bộ với `useConfigSettingTheme` — retry sau collapsible/panel (CustomEvent). */
const POST_LAYOUT_DELAYS_MS = [0, 50, 150, 350] as const;
/** Fallback nếu không có bbox hợp lệ / không nhận event (vd. không mở sidebar). */
const PREVIEW_SCROLL_FALLBACK_MS = 2000;

/** Một số key setting không có trong map nhưng preview gắn với block khác (vd: kích thước ảnh → cột ảnh). */
const RELATED_PREVIEW_SETTING: Record<string, string> = {
  product_image_size: "is_item_thumbnail",
};

type SettingComponentConfig = ( typeof SettingComponentMap )[keyof typeof SettingComponentMap];

function getActionForDataComp( dataComp: string ): string | null {
  const map = SettingComponentMap as Record<string, SettingComponentConfig>;
  const direct = map[dataComp];
  if ( direct && typeof direct.action === "string" && direct.action.length > 0 ) {
    return direct.action;
  }
  const resolved = resolveConfig( dataComp );
  if ( resolved && typeof resolved.action === "string" && resolved.action.length > 0 ) {
    return resolved.action;
  }
  return null;
}

/**
 * Cuộn tới node flash trong shadow preview sau khi layout ổn định:
 * lắng nghe `THEME_COLLAPSIBLE_SECTION_OPENED` + `THEME_SIDEBAR_PANEL_READY` (cùng hệ với deep-link),
 * kèm retry ngắn và fallback — thay cho double rAF.
 */
function scheduleScrollPreviewTargetToView( target: HTMLElement, dataComp: string | null ) {
  const sectionAction = dataComp ? getActionForDataComp( dataComp ) : null;
  const formElement = dataComp ? getFormElementFromDataComp( dataComp ) : null;

  let settled = false;
  /** `window.setTimeout` trả `number` (DOM); tránh `NodeJS.Timeout` khi dùng chung @types/node. */
  const timers: number[] = [];
  let maxTimer: number | null = null;

  const teardown = () => {
    if ( settled ) return;
    settled = true;
    document.removeEventListener( THEME_COLLAPSIBLE_SECTION_OPENED, onCollapsible );
    document.removeEventListener( THEME_SIDEBAR_PANEL_READY, onPanelReady );
    timers.forEach( clearTimeout );
    timers.length = 0;
    if ( maxTimer != null ) {
      clearTimeout( maxTimer );
      maxTimer = null;
    }
  };

  const attemptScroll = () => {
    if ( settled ) return;
    if ( !target.isConnected ) {
      teardown();
      return;
    }
    if ( !isPreviewFlashTargetVisible( target ) ) return;
    const r = target.getBoundingClientRect();
    if ( r.width < 0.5 && r.height < 0.5 ) return;
    scrollPreviewHtmlElementIntoView( target );
    teardown();
  };

  const runRetryBatch = () => {
    queueMicrotask( attemptScroll );
    for ( const ms of POST_LAYOUT_DELAYS_MS ) {
      timers.push( window.setTimeout( attemptScroll, ms ) );
    }
  };

  const onCollapsible = ( ev: Event ) => {
    if ( !sectionAction ) return;
    const d = ( ev as CustomEvent<ThemeCollapsibleSectionOpenedDetail> ).detail;
    if ( d?.sectionId !== sectionAction ) return;
    runRetryBatch();
  };

  const onPanelReady = ( ev: Event ) => {
    if ( !formElement ) return;
    const d = ( ev as CustomEvent<ThemeSidebarPanelReadyDetail> ).detail;
    if ( d?.element !== formElement ) return;
    runRetryBatch();
  };

  if ( sectionAction ) {
    document.addEventListener( THEME_COLLAPSIBLE_SECTION_OPENED, onCollapsible );
  }
  if ( formElement ) {
    document.addEventListener( THEME_SIDEBAR_PANEL_READY, onPanelReady );
  }

  runRetryBatch();

  maxTimer = window.setTimeout( () => {
    if ( !settled && target.isConnected ) {
      scrollPreviewHtmlElementIntoView( target );
    }
    teardown();
  }, PREVIEW_SCROLL_FALLBACK_MS );
}

const PREVIEW_FLASH_STYLE_ID = "__fd_shadow_setting_tooltip__";
const PREVIEW_FLASH_LAYER_CLASS = "fd-preview-setting-flash-layer";
const PREVIEW_FLASH_HIGHLIGHT_CLASS = "fd-preview-setting-flash-highlight";

const SIDEBAR_NAV_OVERLAY_STYLE_ID = "__fd_sidebar_nav_overlay_style__";
const SIDEBAR_OVERLAY_CLASS = "fd-sidebar-nav-overlay";
const SIDEBAR_TAG_CLASS = "fd-sidebar-nav-tag";

let activeNavHighlightComp: string | null = null;
let navHighlightRafId: number | null = null;
let navHighlightRafWin: Window | null = null;
let navHighlightDetachers: (() => void)[] = [];

/** HTML preview thường tới sau API (`useThemeBuilder`); cần đủ lâu cho lần đầu bật option (node chưa có trong DOM cũ). */
const POLL_INTERVAL_MS = 120;
const POLL_MAX_MS = 22_000;

/** RenderHtml: host `.document` → shadow; `[id^="html-"]` nằm *trong* shadow nên không dùng document.querySelector. */
function getThemePreviewShadowRoots(): ShadowRoot[] {
  const hosts = document.querySelectorAll(".document-html .document");
  return Array.from(hosts)
    .map((h) => (h as HTMLElement).shadowRoot)
    .filter((sr): sr is ShadowRoot => !!sr);
}

function ensureSidebarNavOverlayStyles(sr: ShadowRoot) {
  if (sr.getElementById(SIDEBAR_NAV_OVERLAY_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = SIDEBAR_NAV_OVERLAY_STYLE_ID;
  style.textContent = FD_SELECTED_OVERLAY_PAIR_CSS;
  sr.appendChild(style);
}

function ensureSidebarNavOverlayElements(sr: ShadowRoot): {
  overlay: HTMLDivElement;
  tag: HTMLDivElement;
} {
  const doc = sr.host?.ownerDocument || document;
  let overlay = sr.querySelector(`.${SIDEBAR_OVERLAY_CLASS}`) as HTMLDivElement | null;
  if (!overlay) {
    overlay = doc.createElement("div");
    overlay.className = SIDEBAR_OVERLAY_CLASS;
    const tag = doc.createElement("div");
    tag.className = SIDEBAR_TAG_CLASS;
    overlay.appendChild(tag);
    sr.appendChild(overlay);
  }
  const tag = overlay.querySelector(`.${SIDEBAR_TAG_CLASS}`) as HTMLDivElement;
  return { overlay, tag };
}

function syncSidebarNavOverlayInShadowRoot(sr: ShadowRoot, comp: string) {
  ensureSidebarNavOverlayStyles(sr);
  const { overlay, tag } = ensureSidebarNavOverlayElements(sr);
  const raw = [...sr.querySelectorAll<HTMLElement>(`[data-comp="${CSS.escape(comp)}"]`)];
  const nodes = filterToOutermostElements(raw);
  if (nodes.length === 0) {
    overlay.style.display = "none";
    return;
  }
  const rect = unionBoundingRect(nodes);
  if (!rect || (rect.width <= 0 && rect.height <= 0)) {
    overlay.style.display = "none";
    return;
  }
  const host = sr.host as HTMLElement | null;
  updateSelectedOverlayFromRect(overlay, tag, rect, comp, undefined, host);
}

function hideAllSidebarNavOverlays() {
  for (const sr of getThemePreviewShadowRoots()) {
    const overlay = sr.querySelector(`.${SIDEBAR_OVERLAY_CLASS}`) as HTMLDivElement | null;
    if (overlay) overlay.style.display = "none";
  }
}

function getPreviewDefaultView(): Window {
  const srs = getThemePreviewShadowRoots();
  if (srs.length > 0) {
    const d = srs[0].host?.ownerDocument || document;
    return d.defaultView || window;
  }
  return window;
}

function stopNavHighlightMotion() {
  if (navHighlightRafId != null) {
    const w = navHighlightRafWin || window;
    w.cancelAnimationFrame(navHighlightRafId);
    navHighlightRafId = null;
    navHighlightRafWin = null;
  }
  navHighlightDetachers.forEach((d) => d());
  navHighlightDetachers = [];
}

function tickNavHighlight() {
  if (!activeNavHighlightComp) return;
  const win = getPreviewDefaultView();
  navHighlightRafWin = win;
  const srs = getThemePreviewShadowRoots();
  for (const sr of srs) {
    syncSidebarNavOverlayInShadowRoot(sr, activeNavHighlightComp);
  }
  navHighlightRafId = win.requestAnimationFrame(tickNavHighlight);
}

/**
 * Highlight cố định (UI giống `fd-selected-overlay`) khi mở section sidebar.
 * `null` = gỡ hết. Chỉ đổi khi gọi lại với component khác / null.
 */
export function setPreviewDataCompHighlight(comp: string | null) {
  /** Đổi section / tắt highlight → gỡ `fd-selected-overlay` do click trên HTML preview. */
  clearThemePreviewClickSelection();

  stopNavHighlightMotion();
  activeNavHighlightComp = null;
  hideAllSidebarNavOverlays();

  if (!comp) return;

  activeNavHighlightComp = comp;

  const srs = getThemePreviewShadowRoots();
  for (const sr of srs) {
    syncSidebarNavOverlayInShadowRoot(sr, comp);
    const w = sr.host?.ownerDocument?.defaultView || window;
    const detach = attachSelectedLayoutObservers({
      shadowRoot: sr,
      win: w,
      getTargets: () =>
        filterToOutermostElements([
          ...sr.querySelectorAll<HTMLElement>(`[data-comp="${CSS.escape(comp)}"]`),
        ]),
      onUpdate: () => {
        if (activeNavHighlightComp) {
          syncSidebarNavOverlayInShadowRoot(sr, activeNavHighlightComp);
        }
      },
    });
    navHighlightDetachers.push(detach);
  }

  const win = getPreviewDefaultView();
  navHighlightRafWin = win;
  navHighlightRafId = win.requestAnimationFrame(tickNavHighlight);
}

/**
 * Flash khi đổi setting — UI kiểu design tool (Figma / Shopify theme editor):
 * viền xanh mảnh + fill nhẹ, pulse mềm; không mũi tên / không overlay vàng.
 *
 * Layer absolute append cuối shadowRoot — không nằm trong flow invoice.
 * z-index ngang `fd-selected-overlay` để không bị `[data-comp] { z-index: 999 }` che.
 */
const PREVIEW_FLASH_LAYER_CSS = `
@keyframes fd-preview-setting-flash-pulse {
  0% {
    opacity: 0;
  }
  12% {
    opacity: 1;
  }
  40% {
    opacity: 0.55;
  }
  58% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
.${PREVIEW_FLASH_LAYER_CLASS} {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: none;
  overflow: visible;
  pointer-events: none;
  z-index: 2147483644;
}
.${PREVIEW_FLASH_HIGHLIGHT_CLASS} {
  position: absolute;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  pointer-events: none;
  z-index: 2147483645;
  outline: 1px dashed #2c6ecb;
  outline-offset: 1px;
  border-radius: 0;
  background: rgba(44, 110, 203, 0.08);
  opacity: 0;
  animation: fd-preview-setting-flash-pulse 1.8s ease-out forwards;
}
@media (prefers-reduced-motion: reduce) {
  .${PREVIEW_FLASH_HIGHLIGHT_CLASS} {
    animation: none;
    opacity: 1;
  }
}
`;

function ensurePreviewFlashLayerStyles(sr: ShadowRoot) {
  const doc = sr.host?.ownerDocument || document;
  let style = sr.getElementById(PREVIEW_FLASH_STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = doc.createElement("style");
    style.id = PREVIEW_FLASH_STYLE_ID;
    sr.appendChild(style);
  }
  /** Cập nhật nếu CSS đổi (HMR / style cũ còn z-index thấp bị invoice che). */
  if (style.textContent !== PREVIEW_FLASH_LAYER_CSS) {
    style.textContent = PREVIEW_FLASH_LAYER_CSS;
  }
}

function ensurePreviewFlashHostContainingBlock(host: HTMLElement | null, win: Window) {
  if (!host) return;
  /** Cùng pattern `useThemePreview` — chỉ set khi static, tránh đụng layout đã positioned. */
  if (host.style.position === "relative") return;
  if (win.getComputedStyle(host).position === "static") {
    host.style.position = "relative";
  }
}

function syncPreviewFlashHighlightPosition(
  highlight: HTMLDivElement,
  rect: DOMRect,
  host?: HTMLElement | null,
) {
  const local = viewportRectToHostLocal(rect, host);
  if (highlight.style.display !== "block") highlight.style.display = "block";
  /** Cùng padding `fd-selected-overlay` — ring không sát sát nội dung. */
  updateOverlayBox(highlight, local, SELECTED_OVERLAY_PADDING);
}

/** Khớp độ dài animation pulse (~1.8s) + chút buffer. */
const PREVIEW_FLASH_DURATION_MS = 2_000;

/**
 * Node `[data-comp]` có thể vẫn tồn tại trong DOM nhưng bị ẩn (vd. Bootstrap `d-none` khi setting tắt).
 * Nếu flash trên node ẩn, `tryOnce` thành công và dừng poll *trước* khi preview API trả HTML mới
 * → class/animation gắn vào node bị thay thế (Dolphin dùng `{% if %}` nên không gặp; Fordeer dùng `d-none` nên gặp).
 */
function isPreviewFlashTargetVisible(el: HTMLElement): boolean {
  const win = el.ownerDocument?.defaultView;
  if (!win) return false;

  const parentsVisible = (): boolean => {
    let node: Element | null = el;
    while (node) {
      const st = win.getComputedStyle(node as HTMLElement);
      if (st.display === "none" || st.visibility === "hidden") return false;
      node = node.parentElement;
    }
    return true;
  };

  /** `<img>` chưa load / chưa decode thường bbox 0×0 — `checkVisibility` có thể false dù setting đã bật và DOM hợp lệ. */
  if (el instanceof HTMLImageElement) {
    return parentsVisible();
  }

  if (typeof el.checkVisibility === "function") {
    try {
      return el.checkVisibility({
        checkOpacity: false,
        checkVisibilityCSS: true,
        contentVisibilityAuto: true,
      });
    } catch {
      /* fall through */
    }
  }
  return parentsVisible();
}

/** Tất cả key `data-comp` trong preview ứng với một key trong theme.settings (scrollTo). */
export function getDataCompKeysForSetting(settingKey: string): string[] {
  const keys: string[] = [];
  for (const [key, value] of Object.entries(SettingComponentMap)) {
    if (key === "*") continue;
    if (value.scrollTo === settingKey) keys.push(key);
  }
  return keys;
}

function expandSettingKeysForPreview(keys: string[]): string[] {
  const out = new Set<string>();
  for (const k of keys) {
    out.add(k);
    const related = RELATED_PREVIEW_SETTING[k];
    if (related) out.add(related);
  }
  return [...out];
}

/**
 * Flash preview: overlay absolute + bbox trong shadow (CSS pulse), không gắn vào / không sửa DOM invoice.
 */
function flashElement(el: HTMLElement) {
  const root = el.getRootNode();
  if (!(root instanceof ShadowRoot)) return;

  const sr = root;
  const doc = sr.host?.ownerDocument || document;
  const win = doc.defaultView || window;
  const host = sr.host as HTMLElement | null;

  ensurePreviewFlashLayerStyles(sr);
  ensurePreviewFlashHostContainingBlock(host, win);

  const layer = doc.createElement("div");
  layer.className = PREVIEW_FLASH_LAYER_CLASS;
  layer.setAttribute("aria-hidden", "true");
  const highlight = doc.createElement("div");
  highlight.className = PREVIEW_FLASH_HIGHLIGHT_CLASS;
  layer.appendChild(highlight);
  /** Cuối shadowRoot → paint trên nội dung invoice (cùng stacking với selected overlay). */
  sr.appendChild(layer);

  let cleaned = false;
  let detachLayout: () => void = () => {};

  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    detachLayout();
    layer.remove();
  };

  const sync = () => {
    if (!el.isConnected) {
      cleanup();
      return;
    }
    const r = el.getBoundingClientRect();
    if (r.width < 0.5 && r.height < 0.5) return;
    syncPreviewFlashHighlightPosition(highlight, r, host);
    /** Giữ layer cuối shadow nếu HTML inject node mới trong lúc flash. */
    if (layer.parentNode === sr && sr.lastChild !== layer) {
      sr.appendChild(layer);
    }
  };

  sync();

  detachLayout = attachSelectedLayoutObservers({
    shadowRoot: sr,
    win,
    getTargets: () => (el.isConnected ? [el] : []),
    onUpdate: sync,
  });

  win.setTimeout(cleanup, PREVIEW_FLASH_DURATION_MS);
}

function flashTargetsInShadowRoots(
  shadowRoots: ShadowRoot[],
  targetComps: Set<string>
): number {
  let count = 0;
  let firstToScroll: HTMLElement | null = null;
  for ( const sr of shadowRoots ) {
    const nodes = sr.querySelectorAll<HTMLElement>( "[data-comp]" );
    for ( const node of nodes ) {
      const dc = node.getAttribute( "data-comp" );
      if ( !dc || !targetComps.has( dc ) ) continue;
      if ( !isPreviewFlashTargetVisible( node ) ) continue;
      flashElement( node );
      count++;
      if ( !firstToScroll ) firstToScroll = node;
    }
  }
  if ( firstToScroll ) {
    scheduleScrollPreviewTargetToView(
      firstToScroll,
      firstToScroll.getAttribute( "data-comp" )
    );
  }
  return count;
}

/**
 * Highlight các node `[data-comp]` trong preview (shadow DOM).
 * Preview HTML có thể render sau — poll cho tới khi có node hoặc hết thời gian.
 */
function schedulePreviewFlashPolling(
  targetComps: Set<string>,
  options?: { delayMs?: number }
) {
  if (targetComps.size === 0) return;

  const delayMs = options?.delayMs ?? 0;

  let intervalId: number | null = null;
  let maxTimerId: number | null = null;

  const cleanup = () => {
    if (intervalId != null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
    if (maxTimerId != null) {
      window.clearTimeout(maxTimerId);
      maxTimerId = null;
    }
  };

  const tryOnce = (): boolean => {
    const srs = getThemePreviewShadowRoots();
    if (srs.length === 0) return false;
    return flashTargetsInShadowRoots(srs, targetComps) > 0;
  };

  const startPolling = () => {
    const tick = () => {
      if (tryOnce()) {
        cleanup();
      }
    };
    tick();
    intervalId = window.setInterval(tick, POLL_INTERVAL_MS);

    maxTimerId = window.setTimeout(() => {
      cleanup();
    }, POLL_MAX_MS);
  };

  const start = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (tryOnce()) return;
        startPolling();
      });
    });
  };

  if (delayMs > 0) {
    window.setTimeout(start, delayMs);
  } else {
    start();
  }
}

/**
 * Sau khi đổi setting, highlight các node `[data-comp]` tương ứng trong preview (shadow DOM).
 * Preview HTML thường render *sau* `change_theme` (API + RenderHtml) — poll cho tới khi có node hoặc hết thời gian.
 */
export function flashPreviewForSettingKeys(
  changedKeys: string[],
  options?: { delayMs?: number }
) {
  if (!changedKeys.length) return;
  const expanded = expandSettingKeysForPreview(changedKeys);
  const keysToFlash = [
    ...new Set(expanded.filter((k) => getDataCompKeysForSetting(k).length > 0)),
  ];
  if (!keysToFlash.length) return;
  const targetComps = new Set<string>();
  for (const settingKey of keysToFlash) {
    for (const dc of getDataCompKeysForSetting(settingKey)) {
      targetComps.add(dc);
    }
  }
  schedulePreviewFlashPolling(targetComps, options);
}
